import {
  Autocomplete,
  Box,
  Button,
  Chip,
  createFilterOptions,
  Grid,
  IconButton,
  Popover,
  TextField,
  Typography,
  useTheme,
} from '@mui/material'
import React, { useMemo, useRef, useState } from 'react'
import ExitStrategiesProps from './ExitStrategiesProps'
import DeleteIcon from '@mui/icons-material/Delete'
import AddIcon from '@mui/icons-material/Add'
import ForkRightIcon from '@mui/icons-material/ForkRight'
import {
  TradeRatio,
  ConditionalRatio,
  useApplicationSuiteContext,
} from '../../../../contexts/ApplicationSuiteProvider'
import PatternSelect from '../../../../components/form/PatternSelect'
import { PatternVersionSelector } from '../../../../components/form/PatternVersionSelector'
import { usePattern } from '../../../../hooks/usePattern'

const riskRewardOptions: string[] = Array.from({ length: 19 }, (_, i) => {
  i++
  return i <= 10 ? `1/${11 - i}` : `${i - 9}/1`
})

const filter = createFilterOptions<any>()

function ConditionRow({
  condition,
  onUpdate,
  onDelete,
}: {
  condition: ConditionalRatio
  onUpdate: (condition: ConditionalRatio) => void
  onDelete: () => void
}) {
  const theme = useTheme()
  const { usermeta } = useApplicationSuiteContext()
  const [pattern] = usePattern(condition.patternId, condition.patternVersion, condition.gcid)

  const handlePatternChange = (selectedPattern: any) => {
    if (!selectedPattern) return
    onUpdate({
      ...condition,
      patternId: selectedPattern.id,
      patternVersion: selectedPattern.version || 1,
      gcid: selectedPattern.gcid || usermeta?.gcid || '',
    })
  }

  const handleRatioChange = (ratioStr: string) => {
    const [risk, reward] = ratioStr.split('/').map(Number)
    if (!isNaN(risk) && !isNaN(reward)) {
      onUpdate({ ...condition, risk, reward })
    }
  }

  return (
    <Box
      sx={{
        p: 1.5,
        mb: 1,
        borderRadius: 1,
      }}
    >
      <Box display="flex" alignItems="center" gap={1}>
        <Box flex={1}>
          <PatternSelect pattern={pattern} onSelect={handlePatternChange} />
          {pattern?.id && (
            <PatternVersionSelector
              patternId={condition.patternId}
              gcid={usermeta.gcid}
              tradingHouse={pattern?.tradingHouse}
              currentVersion={pattern?.version}
              selectedVersion={condition.patternVersion}
              onVersionSelect={(version) => {
                onUpdate({
                  ...condition,
                  patternVersion: version || pattern?.version || 1,
                })
              }}
            />
          )}
        </Box>
        <Box sx={{ width: 140 }}>
          <Autocomplete
            value={`${condition.risk}/${condition.reward}`}
            onChange={(_, newValue) => {
              if (newValue) handleRatioChange(newValue)
            }}
            options={riskRewardOptions}
            freeSolo
            disableClearable
            renderInput={(params) => <TextField {...params} label="Ratio" />}
          />
        </Box>
        <IconButton
          size="small"
          onClick={onDelete}
          sx={{ mt: 0.5, color: theme.palette.error.main }}
        >
          <DeleteIcon fontSize="small" />
        </IconButton>
      </Box>
    </Box>
  )
}

function RatioChip({
  ratio,
  index,
  onDelete,
  onUpdate,
  onPopoverOpen,
  onPopoverClose,
}: {
  ratio: TradeRatio
  index: number
  onDelete: () => void
  onUpdate: (ratio: TradeRatio) => void
  onPopoverOpen: () => void
  onPopoverClose: () => void
}) {
  const theme = useTheme()
  const [anchorEl, setAnchorEl] = useState<HTMLDivElement | null>(null)

  const hasConditions = ratio.conditions && ratio.conditions.length > 0
  const conditionCount = ratio.conditions?.length || 0

  const handleClick = (event: React.MouseEvent<HTMLDivElement>) => {
    event.stopPropagation()
    event.preventDefault()
    onPopoverOpen()
    setAnchorEl(event.currentTarget)
  }

  const handleClose = () => {
    setAnchorEl(null)
    onPopoverClose()
  }

  const maxConditions = 3

  const handleAddCondition = () => {
    if (conditionCount >= maxConditions) return
    const newCondition: ConditionalRatio = {
      risk: ratio.risk,
      reward: ratio.reward,
      patternId: '',
      patternVersion: 0,
      gcid: '',
      priority: (ratio.conditions?.length || 0) + 1,
    }
    onUpdate({
      ...ratio,
      conditions: [...(ratio.conditions || []), newCondition],
    })
  }

  const handleUpdateCondition = (condIndex: number, updated: ConditionalRatio) => {
    const newConditions = [...(ratio.conditions || [])]
    newConditions[condIndex] = updated
    onUpdate({ ...ratio, conditions: newConditions })
  }

  const handleDeleteCondition = (condIndex: number) => {
    const newConditions = (ratio.conditions || []).filter((_, i) => i !== condIndex)
    newConditions.forEach((c, i) => {
      c.priority = i + 1
    })
    onUpdate({ ...ratio, conditions: newConditions.length > 0 ? newConditions : undefined })
  }

  const popoverOpen = Boolean(anchorEl)

  return (
    <>
      <Chip
        variant="outlined"
        label={
          <Box display="flex" alignItems="center" gap={0.5}>
            <span>
              {ratio.risk}/{ratio.reward}
            </span>
            {hasConditions && (
              <>
                <span>·</span>
                <Box display="flex" alignItems="center">
                  <span>{conditionCount}</span>
                  <ForkRightIcon sx={{ fontSize: 14 }} />
                </Box>
              </>
            )}
          </Box>
        }
        onClick={handleClick}
        onMouseDown={(e) => {
          e.stopPropagation()
          e.preventDefault()
        }}
        onDelete={onDelete}
        sx={{
          cursor: 'pointer',
        }}
      />

      <Popover
        open={popoverOpen}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        disableAutoFocus
        disableEnforceFocus
        disableRestoreFocus
        onClick={(e) => e.stopPropagation()}
        onMouseDown={(e) => e.stopPropagation()}
        PaperProps={{
          sx: {
            width: 420,
            maxHeight: 500,
            p: 2,
            bgcolor: theme.palette.background.custom2,
          },
          onClick: (e: React.MouseEvent) => e.stopPropagation(),
          onMouseDown: (e: React.MouseEvent) => e.stopPropagation(),
        }}
      >
        <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
          Conditional Ratio Override
        </Typography>

        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          You can dynamically set trade ratios that automatically override the {ratio.risk}/
          {ratio.reward} ratio when the pattern criteria are met.
        </Typography>

        {conditionCount === 0 ? (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2, fontStyle: 'italic' }}>
            No conditions defined.
          </Typography>
        ) : (
          <Box sx={{ mb: 2, maxHeight: 300, overflow: 'auto' }}>
            {ratio.conditions?.map((condition, condIndex) => (
              <ConditionRow
                key={condIndex}
                condition={condition}
                onUpdate={(updated) => handleUpdateCondition(condIndex, updated)}
                onDelete={() => handleDeleteCondition(condIndex)}
              />
            ))}
          </Box>
        )}

        <Button
          variant="outlined"
          startIcon={<AddIcon />}
          onClick={handleAddCondition}
          disabled={conditionCount >= maxConditions}
          fullWidth
          size="small"
        >
          Add Condition ({conditionCount}/{maxConditions})
        </Button>
      </Popover>
    </>
  )
}

export default function ConditionalRatioInput(props: ExitStrategiesProps) {
  const {
    backtestSettingInputData,
    handleInputChange,
    formErrors = {},
    multipleTradeRatios,
    symbol: symbolProp,
  } = props
  const { symbols } = useApplicationSuiteContext()
  const [open, setOpen] = useState(false)
  const popoverActiveRef = useRef(false)

  const tradeRatios: TradeRatio[] = backtestSettingInputData.generateTrades.tradeRatios || []
  const isFixed = backtestSettingInputData.generateTrades.exitStrategy === 'fixed'
  const symbolName = symbolProp || backtestSettingInputData.executionSymbol || ''

  const fixedPlaceholder = useMemo(() => {
    if (!isFixed) return ''
    const sym = symbols?.find((s) => s.name === symbolName)
    const decimals = sym?.params?.decimals
    if (decimals != null) {
      const pip = (1 / Math.pow(10, decimals)).toFixed(decimals)
      return `Price (e.g. ${pip}/${pip})`
    }
    if (!symbolName) return 'Price (e.g. .0001/.0002)'
    // Fallback heuristics by symbol name
    const name = symbolName.toUpperCase()
    if (name.includes('JPY')) return 'Price (e.g. .01/.02)'
    if (name.includes('XAU') || name.includes('GOLD')) return 'Price (e.g. 1/2)'
    if (name.includes('XAG') || name.includes('SILVER')) return 'Price (e.g. .01/.02)'
    if (name.includes('BTC')) return 'Price (e.g. 100/200)'
    return 'Price (e.g. .0001/.0002)'
  }, [isFixed, symbols, symbolName])

  function verifyRiskRewardRatio(ratio: string) {
    const regex = /^(\d{1,3}(,\d{3})*(\.\d+)?|\d*\.?\d+)\/(\d{1,3}(,\d{3})*(\.\d+)?|\d*\.?\d+)/
    return regex.test(ratio)
  }

  const handleRatioDelete = (index: number) => {
    const newRatios = tradeRatios.filter((_, i) => i !== index)
    handleInputChange('generateTrades.tradeRatios', newRatios)
  }

  const handleRatioUpdate = (index: number, updatedRatio: TradeRatio) => {
    const newRatios = [...tradeRatios]
    newRatios[index] = updatedRatio
    handleInputChange('generateTrades.tradeRatios', newRatios)
  }

  const totalConditions = tradeRatios.reduce((sum, r) => sum + (r.conditions?.length || 0), 0)

  return (
    <Grid item xs={12}>
      <Autocomplete
        multiple
        freeSolo
        selectOnFocus
        clearOnBlur
        handleHomeEndKeys
        disableCloseOnSelect={multipleTradeRatios}
        open={open}
        onOpen={() => {
          if (popoverActiveRef.current) return
          setOpen(true)
        }}
        onClose={() => {
          if (popoverActiveRef.current) return
          setOpen(false)
        }}
        id="tags-filled"
        onChange={(event, newValue: any) => {
          // Normalize each entry to a ratio string
          const normalize = (val: any): string | null => {
            if (typeof val === 'string') return val
            if (val?.inputValue) return val.inputValue
            if (val?.risk !== undefined) return `${val.risk}/${val.reward}`
            return null
          }

          if (multipleTradeRatios) {
            // Handle additions - preserve existing ratios with conditions
            const existingMap = new Map<string, TradeRatio>()
            tradeRatios.forEach((r) => existingMap.set(`${r.risk}/${r.reward}`, r))

            const newRatios: TradeRatio[] = newValue
              .map((val: any) => {
                // Keep existing TradeRatio objects (with conditions) as-is
                if (typeof val === 'object' && val.risk !== undefined && val.reward !== undefined)
                  return val
                const ratioStr = normalize(val)
                if (!ratioStr || !verifyRiskRewardRatio(ratioStr)) return null
                // Preserve existing ratio with conditions if it exists
                if (existingMap.has(ratioStr)) return existingMap.get(ratioStr)!
                return {
                  risk: parseFloat(ratioStr.split('/')[0]),
                  reward: parseFloat(ratioStr.split('/')[1]),
                }
              })
              .filter(Boolean)
            handleInputChange('generateTrades.tradeRatios', newRatios)
          } else {
            // Single ratio mode - always replace with the latest selection
            if (newValue.length === 0) {
              handleInputChange('generateTrades.tradeRatios', [])
            } else {
              const lastVal = newValue[newValue.length - 1]
              if (
                typeof lastVal === 'object' &&
                lastVal.risk !== undefined &&
                lastVal.reward !== undefined
              ) {
                handleInputChange('generateTrades.tradeRatios', [lastVal])
              } else {
                const ratioStr = normalize(lastVal)
                if (ratioStr && verifyRiskRewardRatio(ratioStr)) {
                  handleInputChange('generateTrades.tradeRatios', [
                    {
                      risk: parseFloat(ratioStr.split('/')[0]),
                      reward: parseFloat(ratioStr.split('/')[1]),
                    },
                  ])
                }
              }
              setOpen(false)
            }
          }
        }}
        filterOptions={(options, params) => {
          const filtered = filter(options, params)
          const { inputValue } = params
          if (inputValue !== '' && verifyRiskRewardRatio(inputValue)) {
            const isExisting = options.some((option) => option === inputValue)
            if (!isExisting) {
              filtered.push({ inputValue, title: `Add "${inputValue}"` })
            }
          }
          return filtered
        }}
        getOptionLabel={(option: any) => {
          if (typeof option === 'string') return option
          if (option.inputValue) return option.inputValue
          if (option.title) return option.title
          return ''
        }}
        options={isFixed ? [] : riskRewardOptions}
        value={tradeRatios.map((r: TradeRatio) => `${r.risk}/${r.reward}`)}
        renderTags={() =>
          tradeRatios.map((ratio: TradeRatio, index: number) => (
            <RatioChip
              key={`${ratio.risk}/${ratio.reward}_${index}`}
              ratio={ratio}
              index={index}
              onDelete={() => handleRatioDelete(index)}
              onUpdate={(updated) => handleRatioUpdate(index, updated)}
              onPopoverOpen={() => {
                popoverActiveRef.current = true
                setOpen(false)
              }}
              onPopoverClose={() => {
                // Brief delay so the Autocomplete doesn't reopen from the popover close click
                requestAnimationFrame(() => {
                  popoverActiveRef.current = false
                })
              }}
            />
          ))
        }
        renderInput={(params) => (
          <TextField
            {...params}
            variant="outlined"
            label={isFixed ? 'Stop Loss / Take Profit' : 'Risk / Reward'}
            error={!!formErrors.generateTrades?.tradeRatios}
            helperText={
              <>
                {formErrors.generateTrades?.tradeRatios
                  ? `${formErrors.generateTrades?.tradeRatios} `
                  : ''}
                {tradeRatios.length} / {multipleTradeRatios ? 15 : 1}
                {totalConditions > 0 && (
                  <span style={{ marginLeft: 8 }}>
                    ({totalConditions} conditional ratio{totalConditions > 1 ? 's' : ''} - click
                    chips to edit)
                  </span>
                )}
              </>
            }
            placeholder={isFixed ? fixedPlaceholder : 'Ratio (e.g. 1/2)'}
          />
        )}
      />
    </Grid>
  )
}
